import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { WpCategory, WpPost } from "@/server/wp.server";

// Post data (full article bodies) is imported dynamically inside handlers only,
// so it stays on the server and never enters the client bundle.
const loadWp = () => import("@/server/wp.server");
const loadPosts = () => import("@/content/posts");

export const listPosts = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z
      .object({
        page: z.number().int().min(1).max(50).optional(),
        perPage: z.number().int().min(1).max(24).optional(),
        categorySlug: z.string().min(1).max(80).optional(),
        locale: z.enum(["en", "bg"]).optional(),
      })
      .parse(data ?? {}),
  )
  .handler(async ({ data }) => {
    const stripBodies = (list: WpPost[]) =>
      list.map((p) => ({ ...p, content: { rendered: "" } }));
    try {
      const { fetchPosts } = await loadWp();
      const { data: posts, totalPages, total } = await fetchPosts(data);
      return { posts: stripBodies(posts), totalPages, total, error: null as string | null };
    } catch (err) {
      console.error("listPosts failed", err);
      // Fall back to local posts so the blog still renders if WP is unreachable.
      const { listLocalPosts } = await loadPosts();
      const local = (data?.page ?? 1) === 1 && !data?.categorySlug ? listLocalPosts() : [];
      return {
        posts: stripBodies(local),
        totalPages: local.length > 0 ? 1 : 0,
        total: local.length,
        error: local.length > 0 ? null : "Could not load posts.",
      };
    }
  });

export const getPost = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ slug: z.string().min(1).max(200), locale: z.enum(["en", "bg"]).optional() }).parse(data),
  )
  .handler(async ({ data }) => {
    try {
      const { fetchPostBySlug } = await loadWp();
      const post = await fetchPostBySlug(data.slug, data.locale);
      if (post) {
        const posts = await loadPosts();
        const loc = data.locale ?? "en";
        const local = posts.getLocalRelatedServices(post.slug, loc);
        post.relatedServices =
          local.length > 0
            ? local
            : posts.getRelatedServicesForCategory(post._embedded?.["wp:term"]?.[0]?.[0]?.slug);
        post.gatedDownload = posts.getLocalGatedDownload(post.slug, loc);
        // Sanitize HTML server-side so we can drop sanitize-html (+deps,
        // ~180 KiB) from the client bundle.
        const { sanitizePostHtml } = await import("@/server/sanitize-html.server");
        post.content.rendered = sanitizePostHtml(post.content.rendered);
      }
      return { post, error: null as string | null };
    } catch (err) {
      console.error("getPost failed", err);
      return { post: null as WpPost | null, error: "Could not load post." };
    }
  });

export const listCategories = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ locale: z.enum(["en", "bg"]).optional() }).parse(data ?? {}),
  )
  .handler(async ({ data }) => {
    try {
      const { fetchCategories } = await loadWp();
      return { categories: await fetchCategories(data.locale), error: null as string | null };
    } catch (err) {
      console.error("listCategories failed", err);
      return { categories: [] as WpCategory[], error: "Could not load categories." };
    }
  });